import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { AdminApprovalType } from '@column/api';
import { Button, Fade, Modal, ModalSize, Tooltip } from '@column/column-ui-kit';
import { Alert, AlertDescription, AlertTitle, Button as UIButton } from '@column/ui';

import { buildUpdateRequestForEntitiesAndAccountsSubset } from '../utils';
import { StateTabs } from '~/app/Layout';
import { useBreadcrumbTitle } from '~/app/Layout/PageBreadcrumbs';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { RoleInfo } from '~/app/pages/Platform/Roles/_partials/RoleInfo';
import { ROUTE } from '~/app/routes';
import { LogoLoading } from '~/elements';
import { useDeleteCustomRole, useGetCustomRole, useUpdateCustomRole } from '~/hooks/useRoles';
import { AdminApprovalReviewDialog, invalidateAdminApprovals, usePendingAdminApprovals } from '~/lib/approvals';
import { useNavigate } from '~/lib/navigation';
import PermissionConfiguration, { Mode, PermissionConfigurationSection, useEntitiesAndAccounts } from '~/lib/roles';
import { RoleRepository, getRoleLabel } from '~/repositories/RoleRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Paragraph } from '~/styles';
import { UpdateCustomRoleRequestObject } from '~/typings/API';

enum Section {
  Info = 'role-info',
  Transfers = 'role-transfers-and-accounts',
  Additional = 'role-additional-permissions',
}

const sections = [
  { label: 'Role Info', value: Section.Info },
  { label: 'Transfers & Accounts', value: Section.Transfers },
  { label: 'Additional Permissions', value: Section.Additional },
];
const sectionValues = sections.map((section) => section.value);

const trimData = (data: UpdateCustomRoleRequestObject): UpdateCustomRoleRequestObject => {
  const output: UpdateCustomRoleRequestObject = {};

  output.name = data.name;
  output.description = data.description ? data.description : '';

  if (data.platformLevelPermissions) {
    output.platformLevelPermissions = Object.fromEntries(
      Object.entries(data.platformLevelPermissions).filter(([, v]) => v !== 'default')
    );
  }
  if (data.entityLevelOverrides) {
    output.entityLevelOverrides = (data.entityLevelOverrides ?? [])
      .map((entity) => {
        return Object.fromEntries(Object.entries(entity).filter(([k, v]) => k === 'entityId' || v !== 'default'));
      })
      .filter((entity) => Object.keys(entity).length > 1);
  }
  if (data.bankAccountLevelOverrides) {
    output.bankAccountLevelOverrides = (data.bankAccountLevelOverrides ?? [])
      .map((bankAccount) => {
        return Object.fromEntries(
          Object.entries(bankAccount).filter(([k, v]) => k === 'bankAccountId' || v !== 'default')
        );
      })
      .filter((entity) => Object.keys(entity).length > 1);
  }

  return output;
};

const ROLE_UPDATE_PENDING_MESSAGE = 'Role changes submitted for approval.';

export const PageEditRole: React.FC = () => {
  const { currentPlatform } = useSessionStore();
  const { roleId } = useParams();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const queryClient = useQueryClient();
  const pendingRoleUpdates = usePendingAdminApprovals(AdminApprovalType.ROLE_UPDATE);
  const pendingApprovals = useMemo(
    () => pendingRoleUpdates.filter((a) => a.request.role_update?.role_id === roleId),
    [pendingRoleUpdates, roleId]
  );
  const pendingApproval = pendingApprovals[0];

  const {
    createRequest: getRole,
    isLoading: isRoleLoading,
    response: role,
  } = useGetCustomRole({
    initialParams: roleId ? { roleId } : undefined,
    shouldNotLoad: !roleId,
    onError: (error) => {
      addDangerNotification({ content: error.message });
    },
  });
  useEffect(() => {
    if (roleId) {
      getRole({ roleId });
    }
  }, [roleId]);

  const updateRole = useUpdateCustomRole({
    onSuccess: (response) => {
      addSuccessNotification({
        content: response.pendingApproval ? ROLE_UPDATE_PENDING_MESSAGE : 'Role updated successfully',
      });
      invalidateAdminApprovals(queryClient);
    },
    onError: (error) => {
      addDangerNotification({ content: error.message });
    },
  });
  const deleteRole = useDeleteCustomRole({
    onSuccess: () => {
      addSuccessNotification({ content: 'Role deleted successfully' });
      navigate(ROUTE.PLATFORM_ROLES);
    },
    onError: (error) => {
      addDangerNotification({ content: error.message });
    },
  });

  const existingBankAccountIDs = useMemo(
    () => role?.bankAccountLevelOverrides?.map((ba) => ba.bankAccountId).filter((id) => typeof id === 'string') ?? [],
    [role]
  );
  const existingEntityIDs = useMemo(
    () => role?.entityLevelOverrides?.map((e) => e.entityId).filter((id) => typeof id === 'string') ?? [],
    [role]
  );

  const sandboxEntitiesAndAccounts = useEntitiesAndAccounts({
    bankAccountIDs: existingBankAccountIDs,
    entityIds: existingEntityIDs,
    mode: Mode.Sandbox,
  });
  const liveEntitiesAndAccounts = useEntitiesAndAccounts({
    bankAccountIDs: existingBankAccountIDs,
    entityIds: existingEntityIDs,
    mode: Mode.Live,
  });

  const [visibleSection, setVisibleSection] = useState<Section>(Section.Info);
  const visiblePermissionSection =
    visibleSection === Section.Transfers
      ? PermissionConfigurationSection.Transfers
      : visibleSection === Section.Additional
        ? PermissionConfigurationSection.Additional
        : null;

  useEffect(() => {
    if (!sectionValues.includes(visibleSection)) {
      setVisibleSection(Section.Info);
    }
  }, [visibleSection]);

  useBreadcrumbTitle(role ? (role.editable ? role.name : getRoleLabel(role.name)) : null);

  const handleCancel = useCallback(() => {
    navigate(ROUTE.PLATFORM_ROLES);
  }, []);
  const handleDelete = useCallback(() => {
    if (!roleId) return;
    deleteRole.createRequest({ roleId });
  }, [deleteRole]);

  const form = useForm<UpdateCustomRoleRequestObject>({
    values: role,
  });
  useEffect(() => {
    form.reset();
  }, [role]);

  const handleSubmit = form.handleSubmit((data: UpdateCustomRoleRequestObject) => {
    if (!roleId) return;
    const trimmedData = trimData(data);

    // Due to some limitations in the API (we can't add both sandbox and live entities/accounts in the same request,
    // we must perform two updates to 1. set the sandbox entities/accounts then 2. set the live entities/accounts.
    if (currentPlatform?.isLiveEnabled) {
      const sandboxData = buildUpdateRequestForEntitiesAndAccountsSubset(trimmedData, sandboxEntitiesAndAccounts);
      RoleRepository.update({ roleId, mode: 'sandbox', ...sandboxData })
        .then((response) => {
          if (!response.pendingApproval) return updateRole.createRequest({ roleId, mode: 'live', ...trimmedData });
          addSuccessNotification({ content: ROLE_UPDATE_PENDING_MESSAGE });
          return invalidateAdminApprovals(queryClient);
        })
        .catch((err) => addDangerNotification({ content: err.message }));
    } else {
      updateRole.createRequest({ roleId, mode: 'sandbox', ...trimmedData });
    }
  });

  return (
    <>
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} size={ModalSize.Small}>
        <Modal.IconCircle type="danger" icon="Warning" />
        <Modal.Content>
          <Paragraph>Are you sure you want to delete this role? This action cannot be reversed.</Paragraph>
        </Modal.Content>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete Role
          </Button>
        </Modal.Footer>
      </Modal>

      <Fade show={isRoleLoading || role === undefined}>
        <LogoLoading className="!top-20" />
      </Fade>
      <Fade show={!isRoleLoading && role !== undefined}>
        <StateTabs
          value={visibleSection}
          onValueChange={(section) => setVisibleSection(section as Section)}
          entries={sections}
        />
        <PageActions>
          <Tooltip
            content={'This role is currently assigned to users. It cannot be deleted until it is unassigned.'}
            isOpen={(role?.userCount ?? 0) > 0 ? undefined : false}
            placement={'bottom'}
          >
            <Button
              isDisabled={(role?.userCount ?? 0) > 0}
              onClick={() => setIsDeleteModalOpen(true)}
              size="small"
              variant="secondary-danger"
            >
              Delete Role
            </Button>
          </Tooltip>
          <Button onClick={handleCancel} size="small" variant="secondary">
            Cancel
          </Button>
          <Tooltip
            content="Resolve the pending change before editing this role."
            isOpen={pendingApproval ? undefined : false}
            placement="bottom"
          >
            <Button
              isDisabled={!!pendingApproval}
              isLoading={!updateRole.isInitialLoad && updateRole.isLoading}
              onClick={handleSubmit}
              size="small"
            >
              Save Role
            </Button>
          </Tooltip>
        </PageActions>
        {pendingApproval && (
          <div className="px-[var(--page-margin)] pt-4 [&+*]:!shadow-none">
            <Alert className="flex items-center justify-between gap-4">
              <div>
                <AlertTitle>
                  {pendingApproval.can_review ? 'Changes to this role need your review' : 'Changes pending approval'}
                </AlertTitle>
                <AlertDescription>
                  Requested {new Date(pendingApproval.created_at).toLocaleString()}. This role can't be edited until the
                  change is approved, rejected, or expires.
                </AlertDescription>
              </div>
              <UIButton
                size="sm"
                variant={pendingApproval.can_review ? 'default' : 'secondary'}
                onClick={() => setIsReviewOpen(true)}
              >
                {pendingApproval.can_review ? 'Review' : 'View'}
              </UIButton>
            </Alert>
          </div>
        )}
        <AdminApprovalReviewDialog
          approvals={pendingApprovals}
          startIndex={0}
          hideCounter
          isOpen={isReviewOpen && !!pendingApproval}
          onClose={() => setIsReviewOpen(false)}
          onReviewed={() => roleId && getRole({ roleId })}
        />
        <FormProvider {...form}>
          {visibleSection === Section.Info && <RoleInfo />}
          {visiblePermissionSection && (
            <PermissionConfiguration
              visibleSection={visiblePermissionSection}
              sandboxEntitiesAndAccounts={sandboxEntitiesAndAccounts}
              liveEntitiesAndAccounts={liveEntitiesAndAccounts}
            />
          )}
        </FormProvider>
      </Fade>
    </>
  );
};
